import { useEffect, useState } from 'react'
import type { AppUiLocale } from '@/i18n/appUiLocale'
import type { HelpArticleMeta, HelpCategoryId, HelpLocale } from './types'
import { HELP_CATEGORY_ORDER } from './types'
import { scoreHelpArticle, tokenizeHelpQuery } from './searchQuery'

export type { HelpArticleMeta, HelpCategoryId, HelpLocale } from './types'
export { HELP_CATEGORY_ORDER } from './types'

const rawLoaders = import.meta.glob('./**/*.md', {
  query: '?raw',
  import: 'default',
}) as Record<string, () => Promise<string>>

function parseFrontmatter(raw: string): { meta: Record<string, string>; body: string } {
  const trimmed = raw.replace(/^\uFEFF/, '')
  if (!trimmed.startsWith('---')) {
    return { meta: {}, body: trimmed.trim() }
  }
  const end = trimmed.indexOf('\n---', 3)
  if (end < 0) {
    return { meta: {}, body: trimmed.trim() }
  }
  const fm = trimmed.slice(3, end).trim()
  const body = trimmed.slice(end + 4).replace(/^\n/, '')
  const meta: Record<string, string> = {}
  for (const line of fm.split('\n')) {
    const idx = line.indexOf(':')
    if (idx <= 0) continue
    const key = line.slice(0, idx).trim()
    const value = line.slice(idx + 1).trim()
    meta[key] = value
  }
  return { meta, body: body.trim() }
}

function pathLocale(path: string): HelpLocale | null {
  if (path.includes('/en/')) return 'en'
  if (path.includes('/zh-Hant/')) return 'zh-Hant'
  if (path.includes('/zh-CN/')) return 'zh-CN'
  return null
}

function toArticle(path: string, raw: string): HelpArticleMeta | null {
  const locale = pathLocale(path)
  if (!locale) return null
  const { meta, body } = parseFrontmatter(raw)
  const category = meta.category as HelpCategoryId | undefined
  const slug = meta.slug
  const id = meta.id
  const title = meta.title
  if (!category || !slug || !id || !title) return null
  if (!HELP_CATEGORY_ORDER.includes(category)) return null
  return {
    id,
    category,
    slug,
    title,
    summary: meta.summary || '',
    dashboardPath: meta.dashboardPath || undefined,
    order: Number(meta.order) || 100,
    locale,
    body,
  }
}

let allArticlesCache: HelpArticleMeta[] | null = null
let loadPromise: Promise<HelpArticleMeta[]> | null = null

/** Load and cache all help markdown (once). Safe to call repeatedly. */
export function ensureHelpArticlesLoaded(): Promise<HelpArticleMeta[]> {
  if (allArticlesCache) return Promise.resolve(allArticlesCache)
  if (!loadPromise) {
    loadPromise = Promise.all(
      Object.entries(rawLoaders).map(async ([path, load]) => {
        const raw = await load()
        return toArticle(path, raw)
      }),
    )
      .then((articles) => {
        allArticlesCache = articles.filter((a): a is HelpArticleMeta => Boolean(a))
        return allArticlesCache
      })
      .catch((err) => {
        loadPromise = null
        throw err
      })
  }
  return loadPromise
}

function localePreference(ui: AppUiLocale): HelpLocale[] {
  if (ui === 'zh-CN') return ['zh-CN', 'zh-Hant', 'en']
  if (ui === 'zh-Hant') return ['zh-Hant', 'en']
  return ['en', 'zh-Hant']
}

function resolveArticlesForUi(uiLocale: AppUiLocale, all: HelpArticleMeta[]): HelpArticleMeta[] {
  const prefs = localePreference(uiLocale)
  const byId = new Map<string, HelpArticleMeta>()
  for (const pref of prefs) {
    for (const article of all) {
      if (article.locale !== pref) continue
      if (!byId.has(article.id)) byId.set(article.id, article)
    }
  }
  return [...byId.values()].sort((a, b) => {
    const cat = HELP_CATEGORY_ORDER.indexOf(a.category) - HELP_CATEGORY_ORDER.indexOf(b.category)
    if (cat !== 0) return cat
    return a.order - b.order || a.title.localeCompare(b.title)
  })
}

export function listHelpArticles(uiLocale: AppUiLocale): HelpArticleMeta[] {
  if (!allArticlesCache) return []
  return resolveArticlesForUi(uiLocale, allArticlesCache)
}

export function listHelpCategoriesWithArticles(uiLocale: AppUiLocale): HelpCategoryId[] {
  const present = new Set(listHelpArticles(uiLocale).map((a) => a.category))
  return HELP_CATEGORY_ORDER.filter((id) => present.has(id))
}

export function getHelpArticlesByCategory(
  uiLocale: AppUiLocale,
  category: HelpCategoryId,
): HelpArticleMeta[] {
  return listHelpArticles(uiLocale).filter((a) => a.category === category)
}

export function getHelpArticle(
  uiLocale: AppUiLocale,
  category: string,
  slug: string,
): HelpArticleMeta | null {
  return (
    listHelpArticles(uiLocale).find((a) => a.category === category && a.slug === slug) ?? null
  )
}

export function getHelpArticleById(uiLocale: AppUiLocale, id: string): HelpArticleMeta | null {
  return listHelpArticles(uiLocale).find((a) => a.id === id) ?? null
}

export function searchHelpArticles(uiLocale: AppUiLocale, query: string): HelpArticleMeta[] {
  const tokens = tokenizeHelpQuery(query)
  if (tokens.length === 0) return listHelpArticles(uiLocale)
  return listHelpArticles(uiLocale)
    .map((article) => ({
      article,
      score: scoreHelpArticle(
        { title: article.title, summary: article.summary, body: article.body },
        query,
      ),
    }))
    .filter((row) => row.score >= 0)
    .sort(
      (a, b) =>
        b.score - a.score ||
        a.article.order - b.article.order ||
        a.article.title.localeCompare(b.article.title),
    )
    .map((row) => row.article)
}

export function helpArticlePath(article: Pick<HelpArticleMeta, 'category' | 'slug'>): string {
  return `/help/${article.category}/${article.slug}`
}

/**
 * Resolve a help URL from article id without loading markdown.
 * Convention: id is `category.slug` (e.g. daily.rental-leases).
 */
export function helpPathFromArticleId(articleId: string): string | null {
  const idx = articleId.indexOf('.')
  if (idx <= 0) return null
  const category = articleId.slice(0, idx)
  const slug = articleId.slice(idx + 1)
  if (!category || !slug) return null
  if (!(HELP_CATEGORY_ORDER as string[]).includes(category)) return null
  return `/help/${category}/${slug}`
}

/** React hook: ensure help articles are loaded for the current UI locale. */
export function useHelpArticlesReady(): boolean {
  const [ready, setReady] = useState(() => Boolean(allArticlesCache))
  useEffect(() => {
    if (allArticlesCache) {
      setReady(true)
      return
    }
    let cancelled = false
    ensureHelpArticlesLoaded()
      .then(() => {
        if (!cancelled) setReady(true)
      })
      .catch((err) => {
        console.error('Failed to load help articles', err)
      })
    return () => {
      cancelled = true
    }
  }, [])
  return ready
}
